import { Button, TextField, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Dialog, DialogForm, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib';
import { formatDate } from '~/lib/datetime';
import type { DeviceCommands__DeviceCommand as DeviceCommand } from '../../../../queries/commands.generated';

const commandSchema = z.object({
  command: z.string(),
  arbitrary: z.string().optional(),
});

export interface DetailsDialogProps {
  open: boolean;
  onClose: () => void;
  row: DeviceCommand | null;
}

export const DetailsDialog = ({ open, onClose, row }: DetailsDialogProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const parsedCommand = commandSchema.parse(row?.command || { command: 'N/A' });

  return row ? (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle onClose={onClose}>
        {t('pages.devices.show.commands.details_title', { name: row.device.name })}
      </DialogTitle>
      <DialogContent>
        <DialogForm>
          <label>
            <span className={`label`}>{t('common.labels.command')}</span>
            <TextField value={parsedCommand.command} disabled fullWidth />
          </label>
          <label>
            <span className={`label`}>{t('common.labels.created_at')}</span>
            <TextField value={formatDate(row.createdAt)} disabled fullWidth />
          </label>
          <label>
            <span className={`label`}>{t('common.labels.expires_at')}</span>
            <TextField value={formatDate(row.expiresAt)} disabled fullWidth />
          </label>
          <label>
            <span className={`label`}>{t('common.labels.status')}</span>
            <TextField value={row.status} disabled fullWidth />
          </label>
          <label>
            <span className={`label`}>{t('common.labels.details')}</span>
            <pre
              style={{
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${theme.palette.borders.light}`,
                borderRadius: '4px',
                color: theme.palette.text.primary,
                fontSize: '.9em',
                width: '100%',
                padding: '8px',
              }}
            >
              {JSON.stringify(row.command || {}, null, 2)}
            </pre>
          </label>
        </DialogForm>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          {t('common.dialogs.close')}
        </Button>
      </DialogActions>
    </Dialog>
  ) : null;
};
